import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Briefcase, Info, DollarSign, ArrowRight, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import type { DailyPaycheck } from "@/hooks/useDailyPaycheck";
import { useAccounts } from "@/hooks/useAccounts";
import { getErrorMessage } from "@/lib/errors";

/**
 * Daily paycheck reveal + allocation flow.
 * Stages: arrival → gross → deductions animate → net → allocation → done.
 * Server enforces idempotency; this UI just walks the learner through it.
 */

interface Props {
  paycheck: DailyPaycheck;
  onAllocate: (allocation: Record<string, number>) => Promise<void>;
  onClose: () => void;
}

const DEDUCTION_META: Record<string, { label: string; what: string; why: string; usedFor: string }> = {
  federal_tax: {
    label: "Federal income tax",
    what: "Tax withheld for the federal government.",
    why: "Employers withhold an estimate of what you'll owe at tax time.",
    usedFor: "National programs — defense, transportation, federal services.",
  },
  state_tax: {
    label: "State income tax",
    what: "Tax withheld for your state government.",
    why: "Most states levy income tax; a few states have none.",
    usedFor: "Schools, roads, public safety, Medicaid match.",
  },
  social_security: {
    label: "Social Security (FICA)",
    what: "6.2% payroll tax.",
    why: "Mandatory contribution to the federal retirement & disability system.",
    usedFor: "Pays current retirees, disabled workers, and survivors.",
  },
  medicare: {
    label: "Medicare",
    what: "1.45% payroll tax.",
    why: "Funds health coverage for Americans 65+.",
    usedFor: "Hospital insurance and medical coverage for seniors.",
  },
  benefits: {
    label: "Benefits / insurance",
    what: "Pre-tax employer health, dental, and vision premiums.",
    why: "Employers split the cost of group health coverage with you.",
    usedFor: "Medical, dental, and vision care for you and dependents.",
  },
  retirement: {
    label: "Retirement contribution",
    what: "Pre-tax 401(k) or similar contribution.",
    why: "Building retirement savings before money hits checking.",
    usedFor: "Long-term investments for your future self.",
  },
};

const ALLOC_TARGETS = [
  { type: "checking", label: "Checking" },
  { type: "savings", label: "Savings" },
  { type: "emergency_fund", label: "Emergency Fund" },
  { type: "investment", label: "Investing" },
];

export default function DailyPaycheckModal({ paycheck, onAllocate, onClose }: Props) {
  const { accounts } = useAccounts();
  const [stage, setStage] = useState<"arrival" | "gross" | "deductions" | "net" | "allocate">("arrival");
  const [allocation, setAllocation] = useState<Record<string, number>>(() => ({
    checking: Number(paycheck.net_pay.toFixed(2)),
    savings: 0,
    emergency_fund: 0,
    investment: 0,
  }));
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setStage("gross"), 900);
    const t2 = setTimeout(() => setStage("deductions"), 2100);
    const t3 = setTimeout(() => setStage("net"), 4600);
    const t4 = setTimeout(() => setStage("allocate"), 6200);
    return () => { [t1, t2, t3, t4].forEach(clearTimeout); };
  }, []);

  const deductionRows = useMemo(() => [
    ["federal_tax", paycheck.federal_tax],
    ["state_tax", paycheck.state_tax],
    ["social_security", paycheck.social_security],
    ["medicare", paycheck.medicare],
    ["benefits", paycheck.benefits],
    ["retirement", paycheck.retirement],
  ] as [string, number][], [paycheck]);

  const totalAllocated = Object.values(allocation).reduce((s, n) => s + (Number(n) || 0), 0);
  const remaining = Number((paycheck.net_pay - totalAllocated).toFixed(2));
  const balanced = Math.abs(remaining) < 0.01;

  const setAlloc = (k: string, v: number) =>
    setAllocation(prev => ({ ...prev, [k]: Math.max(0, Number(v.toFixed(2))) }));

  const allToChecking = () => setAllocation({
    checking: Number(paycheck.net_pay.toFixed(2)), savings: 0, emergency_fund: 0, investment: 0,
  });
  const fiftyThirtyTwenty = () => {
    const n = paycheck.net_pay;
    const c = Number((n * 0.5).toFixed(2));
    const e = Number((n * 0.2).toFixed(2));
    const s = Number((n * 0.2).toFixed(2));
    const i = Number((n - c - e - s).toFixed(2));
    setAllocation({ checking: c, emergency_fund: e, savings: s, investment: i });
  };

  const handleSubmit = async () => {
    if (!balanced) {
      toast.error("Allocate the full net check before saving");
      return;
    }
    setSubmitting(true);
    try {
      await onAllocate(allocation);
      toast.success("Paycheck allocated");
      onClose();
    } catch (err: unknown) {
      toast.error(getErrorMessage(err) || "Could not allocate paycheck");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <TooltipProvider delayDuration={150}>
      <div className="fixed inset-0 z-[160] flex items-end justify-center p-3 sm:items-center sm:p-4">
        <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" />
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          className="relative w-full max-w-md max-h-[92vh] overflow-y-auto rounded-2xl border-2 border-primary/30 bg-card p-5 shadow-[0_0_60px_hsl(var(--primary)/0.18)]"
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="h-9 w-9 rounded-xl bg-primary/15 flex items-center justify-center">
              <Briefcase className="h-4 w-4 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] tracking-[0.2em] uppercase text-primary/80 font-semibold">Your check arrived</p>
              <p className="text-xs text-muted-foreground truncate">
                {paycheck.career_key_snapshot.replace(/_/g, " ")} · {paycheck.pay_date}
              </p>
            </div>
          </div>

          <AnimatePresence mode="wait">
            {stage === "arrival" && (
              <motion.div key="arrival" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="py-10 text-center">
                <DollarSign className="mx-auto h-10 w-10 text-primary animate-pulse" />
                <p className="mt-4 text-sm text-muted-foreground">Calculating today's pay…</p>
              </motion.div>
            )}

            {stage !== "arrival" && (
              <motion.div key="check" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
                <div className="rounded-xl border border-border/60 bg-muted/30 p-4">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>Gross pay</span>
                    <span className="font-mono text-foreground">${paycheck.gross_pay.toFixed(2)}</span>
                  </div>

                  {stage !== "gross" && (
                    <div className="mt-3 space-y-1.5 border-t border-border/60 pt-3">
                      {deductionRows.map(([key, amt], idx) => {
                        const meta = DEDUCTION_META[key];
                        return (
                          <motion.div key={key}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: idx * 0.18 }}
                            className="flex items-center justify-between text-[11px]">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button className="flex items-center gap-1 text-muted-foreground hover:text-foreground">
                                  {meta.label}
                                  <Info className="h-3 w-3 opacity-60" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="right" className="max-w-[240px] text-[11px] leading-relaxed">
                                <p className="font-semibold">{meta.label}</p>
                                <p className="mt-1 opacity-90">{meta.what}</p>
                                <p className="mt-1 opacity-80">Why: {meta.why}</p>
                                <p className="mt-1 opacity-80">Used for: {meta.usedFor}</p>
                                <p className="mt-2 text-amber-400">Educational estimate — not tax advice.</p>
                              </TooltipContent>
                            </Tooltip>
                            <span className="font-mono text-destructive/90">−${amt.toFixed(2)}</span>
                          </motion.div>
                        );
                      })}
                    </div>
                  )}

                  {(stage === "net" || stage === "allocate") && (
                    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                      className="mt-3 flex items-center justify-between border-t border-primary/30 pt-3">
                      <span className="text-xs font-semibold text-foreground">Net pay</span>
                      <span className="font-heading text-xl font-bold text-gradient-blue">
                        ${paycheck.net_pay.toFixed(2)}
                      </span>
                    </motion.div>
                  )}
                </div>

                {stage === "allocate" && (
                  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-foreground">Where should it go?</p>
                      <div className="flex gap-1">
                        <button onClick={allToChecking}
                          className="rounded-md border border-border px-2 py-1 text-[10px] hover:bg-muted/50">
                          All checking
                        </button>
                        <button onClick={fiftyThirtyTwenty}
                          className="rounded-md border border-border px-2 py-1 text-[10px] hover:bg-muted/50">
                          50/20/20/10
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      {ALLOC_TARGETS.map(({ type, label }) => {
                        const acct = accounts.find(a => a.account_type === type);
                        const locked = acct?.status === "locked";
                        return (
                          <div key={type}
                            className="flex items-center gap-2 rounded-xl border border-border bg-card/60 px-3 py-2">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs font-semibold text-foreground">{label}</span>
                                {locked && (
                                  <span className="inline-flex items-center gap-0.5 rounded bg-amber-500/15 px-1.5 py-0.5 text-[9px] text-amber-400">
                                    <Lock className="h-2.5 w-2.5" /> Pending — releases on unlock
                                  </span>
                                )}
                              </div>
                              <p className="text-[10px] text-muted-foreground">
                                Balance ${acct ? Number(acct.balance).toFixed(2) : "0.00"}
                              </p>
                            </div>
                            <input type="number" inputMode="decimal" min={0}
                              step="0.01"
                              value={allocation[type] ?? 0}
                              onChange={(e) => setAlloc(type, Number(e.target.value || 0))}
                              className="w-24 rounded-md border border-border bg-background px-2 py-1 text-right text-xs font-mono"
                            />
                          </div>
                        );
                      })}
                    </div>

                    <div className="flex items-center justify-between rounded-xl border border-border/60 bg-muted/30 px-3 py-2 text-xs">
                      <span className="text-muted-foreground">Remaining to allocate</span>
                      <span className={`font-mono font-semibold ${balanced ? "text-primary" : "text-destructive"}`}>
                        ${remaining.toFixed(2)}
                      </span>
                    </div>

                    <Button onClick={handleSubmit} disabled={!balanced || submitting} className="w-full gap-2">
                      Save allocation <ArrowRight className="h-4 w-4" />
                    </Button>
                    <p className="text-center text-[10px] text-muted-foreground">
                      Educational estimate. Locked accounts still receive their share.
                    </p>
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </TooltipProvider>
  );
}
